/**
 * LiveTwinDemo — iter635 + iter636 (Personalized Demo Mode).
 *
 * Landing page live, no-login mini-demo. Показва как Кардио + Стрес близнаци
 * реагират в реално време на симулирани събития. От iter636 насам, преди да
 * стартира демото, пита посетителя 3 кратки въпроса (възраст, пол, основен
 * симптом) и динамично коригира baseline и таргетите така, че числата да са
 * правдоподобни за неговия профил.
 *
 * Без backend калове, без login. Чисто frontend.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Zap, Coffee, Wind, Sparkles, ArrowRight, RotateCcw } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────
// Baseline tuning by demographics + chief complaint.
// Returns a full set of {baseline, stress, coffee, breath, rest} targets.
// ─────────────────────────────────────────────────────────────────────
function buildProfileTargets(profile) {
  // Default (no profile yet) — healthy 35y baseline.
  if (!profile) {
    return {
      baseline: { bp_sys: 118, bp_dia: 75, hr: 68, hrv: 55, cardio_score: 90, stress_score: 78 },
      stress:   { bp_sys: 148, bp_dia: 95, hr: 92, hrv: 28, cardio_score: 55, stress_score: 38 },
      coffee:   { bp_sys: 128, bp_dia: 82, hr: 85, hrv: 42, cardio_score: 72, stress_score: 60 },
      breath:   { bp_sys: 112, bp_dia: 72, hr: 58, hrv: 72, cardio_score: 95, stress_score: 92 },
      rest:     { bp_sys: 118, bp_dia: 75, hr: 68, hrv: 55, cardio_score: 90, stress_score: 78 },
    };
  }
  const age = Number(profile.age) || 35;
  const isFemale = profile.gender === "female";
  const symptom = profile.symptom || "energy";

  // Start with a clean healthy baseline, then apply demographic + symptom modifiers.
  let bp_sys = 118 + Math.max(0, (age - 30)) * 0.45;   // +0.45 mmHg per year above 30
  let bp_dia = 75 + Math.max(0, (age - 30)) * 0.25;
  let hr = isFemale ? 72 : 66;                          // women slightly higher resting HR
  let hrv = Math.max(22, 65 - Math.max(0, (age - 25)) * 0.6); // HRV declines with age
  let cardio = 92 - Math.max(0, (age - 30)) * 0.4;
  let stress = 80;

  // Chief-complaint modifiers
  switch (symptom) {
    case "energy":         // умора / ниска енергия
      hrv -= 8;  cardio -= 8;  stress -= 14; hr += 4;
      break;
    case "stress":         // тревожност / висок стрес
      hrv -= 14; stress -= 28; hr += 8; bp_sys += 6; bp_dia += 4; cardio -= 6;
      break;
    case "sleep":          // лош сън
      hrv -= 10; stress -= 18; cardio -= 7; hr += 3;
      break;
    case "cardio":         // сърцебиене / хипертония
      bp_sys += 14; bp_dia += 9; hr += 6; cardio -= 18; hrv -= 6; stress -= 6;
      break;
    case "other":
    default:
      // mild generic skew
      cardio -= 4; stress -= 6; hrv -= 3;
  }

  // Round + clamp
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  const baseline = {
    bp_sys: Math.round(clamp(bp_sys, 95, 175)),
    bp_dia: Math.round(clamp(bp_dia, 60, 110)),
    hr: Math.round(clamp(hr, 50, 105)),
    hrv: Math.round(clamp(hrv, 15, 95)),
    cardio_score: Math.round(clamp(cardio, 30, 100)),
    stress_score: Math.round(clamp(stress, 25, 100)),
  };

  // Event targets — apply relative deltas to the personalised baseline.
  const delta = (d) => ({
    bp_sys: clamp(baseline.bp_sys + d.bp_sys, 90, 200),
    bp_dia: clamp(baseline.bp_dia + d.bp_dia, 55, 120),
    hr: clamp(baseline.hr + d.hr, 45, 130),
    hrv: clamp(baseline.hrv + d.hrv, 10, 100),
    cardio_score: clamp(baseline.cardio_score + d.cardio_score, 20, 100),
    stress_score: clamp(baseline.stress_score + d.stress_score, 15, 100),
  });

  return {
    baseline,
    stress: delta({ bp_sys: 28, bp_dia: 18, hr: 22, hrv: -22, cardio_score: -28, stress_score: -38 }),
    coffee: delta({ bp_sys: 10, bp_dia: 6,  hr: 14, hrv: -10, cardio_score: -14, stress_score: -16 }),
    breath: delta({ bp_sys: -8, bp_dia: -4, hr: -10, hrv: 18, cardio_score: 8,  stress_score: 16 }),
    rest:   baseline,
  };
}

// ─────────────────────────────────────────────────────────────────────
// Symptom catalogue (BG / EN labels + descriptive impact line shown in
// the "personalised for you" badge once the form is submitted).
// ─────────────────────────────────────────────────────────────────────
const SYMPTOMS = {
  bg: [
    { key: "energy",  label: "Умора и ниска енергия",   icon: "⚡" },
    { key: "stress",  label: "Тревожност / висок стрес", icon: "🔥" },
    { key: "sleep",   label: "Лош сън",                  icon: "🌙" },
    { key: "cardio",  label: "Сърцебиене / високо BP",   icon: "🫀" },
    { key: "other",   label: "Друго / общо здраве",      icon: "✨" },
  ],
  en: [
    { key: "energy",  label: "Fatigue / low energy",       icon: "⚡" },
    { key: "stress",  label: "Anxiety / high stress",      icon: "🔥" },
    { key: "sleep",   label: "Poor sleep",                 icon: "🌙" },
    { key: "cardio",  label: "Palpitations / high BP",     icon: "🫀" },
    { key: "other",   label: "Other / general wellness",   icon: "✨" },
  ],
};

export default function LiveTwinDemo({ isDark, bg = true }) {
  const T = bg ? {
    eyebrow: "🔴 На живо · без регистрация",
    title: "Виж как реагират близнаците в реално време",
    sub: "Натисни едно от събитията долу и наблюдавай как BP, HRV и кортизол скорове се променят моментално — точно както при истински пациент.",
    cardio: "Кардиоваскуларен",
    stress: "Стрес",
    bp: "BP", hr: "HR", hrv: "HRV", cortisol: "Кортизол",
    eventStress: "Стрес момент",
    eventCoffee: "Кафе",
    eventBreath: "4-7-8 дишане",
    eventRest: "Покой",
    twin_score: "Twin Score",
    cta: "Виж пълния си близнак →",
    log_prefix: "Симулирано събитие:",
    // Personalized mode
    personalize_title: "Преди да започнем — кажи ни малко за теб",
    personalize_sub: "3 кратки въпроса. Без регистрация. Демото ще се настрои за твоя профил.",
    q_age: "Възраст",
    q_age_ph: "напр. 47",
    q_gender: "Пол",
    gender_female: "Жена",
    gender_male: "Мъж",
    gender_other: "Друго / не уточнявам",
    q_symptom: "Кое те вълнува най-много в момента?",
    btn_start: "Стартирай персонализирано демо",
    btn_skip: "Пропусни — общо демо",
    badge_for: "Персонализирано за",
    yo: "г.",
    reset: "Промени профила",
    custom_card: "Демото е настроено за",
  } : {
    eyebrow: "🔴 Live · no signup",
    title: "Watch the twins react in real time",
    sub: "Click one of the events below and see BP, HRV and cortisol scores shift instantly — just like a real patient.",
    cardio: "Cardiovascular",
    stress: "Stress",
    bp: "BP", hr: "HR", hrv: "HRV", cortisol: "Cortisol",
    eventStress: "Stress event",
    eventCoffee: "Coffee",
    eventBreath: "4-7-8 breathing",
    eventRest: "Rest",
    twin_score: "Twin Score",
    cta: "See your full twin →",
    log_prefix: "Simulated event:",
    personalize_title: "Before we start — tell us a bit about you",
    personalize_sub: "3 short questions. No signup. The demo will tune itself for your profile.",
    q_age: "Age",
    q_age_ph: "e.g. 47",
    q_gender: "Sex",
    gender_female: "Female",
    gender_male: "Male",
    gender_other: "Other / prefer not to say",
    q_symptom: "What concerns you most right now?",
    btn_start: "Start personalised demo",
    btn_skip: "Skip — generic demo",
    badge_for: "Personalised for",
    yo: "yo",
    reset: "Edit profile",
    custom_card: "Demo tuned for",
  };
  const symptoms = SYMPTOMS[bg ? "bg" : "en"];

  // Personalisation state
  const [profile, setProfile] = useState(null);              // null until submitted
  const [draft, setDraft] = useState({ age: "", gender: "female", symptom: "energy" });

  const TARGETS = useMemo(() => buildProfileTargets(profile), [profile]);

  // Live twin animation state — initialised from current TARGETS.baseline.
  // When profile changes, handleSubmitProfile / resetProfile update state + ref
  // directly (no useEffect needed). This keeps the convergence loop happy.
  const [state, setState] = useState(TARGETS.baseline);
  const [log, setLog] = useState([]);
  const [pulse, setPulse] = useState(false);
  const targetRef = useRef(TARGETS.baseline);

  // Animation loop — slow-converge to target values
  useEffect(() => {
    const id = setInterval(() => {
      setState((cur) => {
        const tgt = targetRef.current;
        const step = (a, b, k = 0.18) => a + (b - a) * k;
        return {
          bp_sys: Math.round(step(cur.bp_sys, tgt.bp_sys) * 10) / 10,
          bp_dia: Math.round(step(cur.bp_dia, tgt.bp_dia) * 10) / 10,
          hr: Math.round(step(cur.hr, tgt.hr) * 10) / 10,
          hrv: Math.round(step(cur.hrv, tgt.hrv) * 10) / 10,
          cardio_score: Math.round(step(cur.cardio_score, tgt.cardio_score)),
          stress_score: Math.round(step(cur.stress_score, tgt.stress_score)),
        };
      });
    }, 250);
    return () => clearInterval(id);
  }, []);

  // Heart pulse animation tied to HR
  useEffect(() => {
    const beat = 60000 / Math.max(40, state.hr);
    const id = setInterval(() => setPulse((p) => !p), beat);
    return () => clearInterval(id);
  }, [state.hr]);

  const trigger = (key) => {
    targetRef.current = TARGETS[key];
    const labels = { stress: T.eventStress, coffee: T.eventCoffee, breath: T.eventBreath, rest: T.eventRest };
    const newLog = [{ ts: new Date().toLocaleTimeString(bg ? "bg-BG" : "en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" }), name: labels[key] }, ...log].slice(0, 4);
    setLog(newLog);
  };

  const applyProfile = (p) => {
    const targets = buildProfileTargets(p);
    setProfile(p);
    setState(targets.baseline);
    targetRef.current = targets.baseline;
    setLog([]);
  };

  const handleSubmitProfile = (e) => {
    e?.preventDefault?.();
    const age = parseInt(draft.age, 10);
    applyProfile({
      age: Number.isFinite(age) && age >= 12 && age <= 100 ? age : 35,
      gender: draft.gender,
      symptom: draft.symptom,
    });
  };

  const skipPersonalize = () => {
    // Use a sensible "general adult" baseline
    applyProfile({ age: 35, gender: "female", symptom: "other" });
  };

  const resetProfile = () => {
    setProfile(null);
    setLog([]);
  };

  const scoreColor = (s) => s >= 80 ? "text-emerald-500" : s >= 60 ? "text-amber-500" : "text-rose-500";
  const bg_score_bar = (s) => s >= 80 ? "bg-emerald-500" : s >= 60 ? "bg-amber-500" : "bg-rose-500";

  const sectionBg = isDark ? "bg-[hsl(223,47%,8%)]" : "bg-gradient-to-b from-white to-fuchsia-50/30";
  const cardBg = isDark ? "bg-[hsl(223,47%,9%)]" : "bg-white shadow-sm";

  // ─── Onboarding form (shown only before profile is submitted) ────────
  if (!profile) {
    const selectedSymptom = symptoms.find((s) => s.key === draft.symptom);
    return (
      <section data-testid="live-twin-demo" className={`py-16 sm:py-20 ${sectionBg}`}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-7">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest mb-3 px-3 py-1 rounded-full" style={{ background: isDark ? "rgba(244, 63, 94, 0.15)" : "rgba(244, 63, 94, 0.10)", color: "var(--brand-pink)" }}>
              <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
              {T.eyebrow}
            </div>
            <h2 className={`text-3xl sm:text-4xl font-semibold mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
              {T.personalize_title}
            </h2>
            <p className={`text-sm sm:text-base max-w-2xl mx-auto ${isDark ? "text-slate-400" : "text-slate-600"}`}>{T.personalize_sub}</p>
          </div>

          <form
            onSubmit={handleSubmitProfile}
            data-testid="demo-onboarding-form"
            className={`p-6 sm:p-8 rounded-2xl border ${isDark ? "border-fuchsia-500/30" : "border-fuchsia-200"} ${cardBg}`}
          >
            <div className="grid sm:grid-cols-2 gap-5">
              {/* Age */}
              <label className="block">
                <div className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? "text-slate-300" : "text-slate-700"}`}>{T.q_age}</div>
                <input
                  type="number"
                  min={12}
                  max={100}
                  inputMode="numeric"
                  required
                  value={draft.age}
                  onChange={(e) => setDraft((d) => ({ ...d, age: e.target.value }))}
                  placeholder={T.q_age_ph}
                  data-testid="demo-onboard-age"
                  className={`w-full px-4 py-3 rounded-xl text-base font-semibold border focus:outline-none focus:ring-2 focus:ring-fuchsia-400 ${isDark ? "bg-[hsl(223,47%,7%)] border-slate-700 text-white placeholder:text-slate-500" : "bg-white border-slate-200 text-slate-900 placeholder:text-slate-400"}`}
                />
              </label>
              {/* Gender */}
              <div>
                <div className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? "text-slate-300" : "text-slate-700"}`}>{T.q_gender}</div>
                <div className="flex flex-wrap gap-2" data-testid="demo-onboard-gender-group">
                  {[
                    { key: "female", label: T.gender_female },
                    { key: "male", label: T.gender_male },
                    { key: "other", label: T.gender_other },
                  ].map((g) => (
                    <button
                      key={g.key}
                      type="button"
                      onClick={() => setDraft((d) => ({ ...d, gender: g.key }))}
                      data-testid={`demo-onboard-gender-${g.key}`}
                      className={`px-3 py-2 rounded-full text-xs font-bold border transition-colors ${draft.gender === g.key
                        ? "border-transparent text-white"
                        : isDark
                          ? "border-slate-700 text-slate-300 hover:bg-white/5"
                          : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}
                      style={draft.gender === g.key ? { background: "var(--brand-gradient-primary)" } : undefined}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Symptom */}
            <div className="mt-6">
              <div className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? "text-slate-300" : "text-slate-700"}`}>{T.q_symptom}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2" data-testid="demo-onboard-symptom-group">
                {symptoms.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setDraft((d) => ({ ...d, symptom: s.key }))}
                    data-testid={`demo-onboard-symptom-${s.key}`}
                    className={`p-3 rounded-xl text-xs font-semibold text-left border transition-colors ${draft.symptom === s.key
                      ? isDark
                        ? "border-fuchsia-500/60 bg-fuchsia-500/15 text-white"
                        : "border-fuchsia-300 bg-fuchsia-50 text-fuchsia-900"
                      : isDark
                        ? "border-slate-700 text-slate-300 hover:bg-white/5"
                        : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}
                  >
                    <div className="text-lg mb-0.5">{s.icon}</div>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row gap-3 items-center justify-center">
              <button
                type="submit"
                data-testid="demo-onboard-submit"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold text-white shadow-md hover:shadow-lg transition-shadow"
                style={{ background: "var(--brand-gradient-primary)" }}
              >
                <Sparkles className="w-4 h-4" />
                {T.btn_start}
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={skipPersonalize}
                data-testid="demo-onboard-skip"
                className={`text-xs font-semibold underline-offset-4 hover:underline ${isDark ? "text-slate-400" : "text-slate-500"}`}
              >
                {T.btn_skip}
              </button>
            </div>

            {selectedSymptom && draft.age && (
              <p className={`text-[11px] text-center mt-5 opacity-80 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                {T.badge_for} {draft.age} {T.yo} · {draft.gender === "female" ? T.gender_female : draft.gender === "male" ? T.gender_male : T.gender_other} · {selectedSymptom.label}
              </p>
            )}
          </form>
        </div>
      </section>
    );
  }

  // ─── Live demo section (after profile submitted) ────────────────────
  const symptomLabel = symptoms.find((s) => s.key === profile.symptom)?.label || profile.symptom;
  const genderLabel =
    profile.gender === "female" ? T.gender_female : profile.gender === "male" ? T.gender_male : T.gender_other;

  return (
    <section data-testid="live-twin-demo" className={`py-16 sm:py-20 ${sectionBg}`}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest mb-3 px-3 py-1 rounded-full" style={{ background: isDark ? "rgba(244, 63, 94, 0.15)" : "rgba(244, 63, 94, 0.10)", color: "var(--brand-pink)" }}>
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
            {T.eyebrow}
          </div>
          <h2 className={`text-3xl sm:text-4xl font-semibold mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`} style={{ fontFamily: "'Work Sans', sans-serif" }}>
            {T.title}
          </h2>
          <p className={`text-sm sm:text-base max-w-2xl mx-auto ${isDark ? "text-slate-400" : "text-slate-600"}`}>{T.sub}</p>

          {/* Personalised-for badge */}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2" data-testid="demo-personalized-badge">
            <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold border ${isDark ? "bg-fuchsia-500/10 text-fuchsia-200 border-fuchsia-500/30" : "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200"}`}>
              <Sparkles className="w-3 h-3" />
              {T.custom_card}: <strong>{profile.age} {T.yo}</strong> · {genderLabel} · {symptomLabel}
            </div>
            <button
              type="button"
              onClick={resetProfile}
              data-testid="demo-reset-profile"
              className={`inline-flex items-center gap-1 text-[11px] font-semibold underline-offset-4 hover:underline ${isDark ? "text-slate-400" : "text-slate-500"}`}
            >
              <RotateCcw className="w-3 h-3" />
              {T.reset}
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-5">
          {/* Cardio Twin */}
          <div className={`p-5 rounded-2xl border ${isDark ? "bg-[hsl(223,47%,9%)] border-rose-700/40" : "bg-white border-rose-200 shadow-sm"}`} data-testid="demo-cardio-card">
            <div className="flex items-center gap-2 mb-3">
              <Heart className={`w-5 h-5 text-rose-500 transition-transform duration-150 ${pulse ? "scale-125" : "scale-100"}`} />
              <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>🫀 {T.cardio}</h3>
              <span className={`ml-auto text-2xl font-bold tabular-nums transition-colors ${scoreColor(state.cardio_score)}`} data-testid="demo-cardio-score">{state.cardio_score}/100</span>
            </div>
            <div className={`h-2 rounded-full overflow-hidden mb-3 ${isDark ? "bg-slate-800" : "bg-slate-100"}`}>
              <div className={`h-full transition-all duration-500 ${bg_score_bar(state.cardio_score)}`} style={{ width: `${state.cardio_score}%` }} />
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className={`p-2 rounded-lg ${isDark ? "bg-slate-800/60" : "bg-rose-50"}`}>
                <div className="text-[9px] uppercase opacity-70 tracking-wider">{T.bp}</div>
                <div className="text-base font-bold tabular-nums">{Math.round(state.bp_sys)}/{Math.round(state.bp_dia)}</div>
              </div>
              <div className={`p-2 rounded-lg ${isDark ? "bg-slate-800/60" : "bg-rose-50"}`}>
                <div className="text-[9px] uppercase opacity-70 tracking-wider">{T.hr}</div>
                <div className="text-base font-bold tabular-nums">{Math.round(state.hr)}</div>
              </div>
              <div className={`p-2 rounded-lg ${isDark ? "bg-slate-800/60" : "bg-rose-50"}`}>
                <div className="text-[9px] uppercase opacity-70 tracking-wider">{T.hrv}</div>
                <div className="text-base font-bold tabular-nums">{Math.round(state.hrv)}</div>
              </div>
            </div>
          </div>

          {/* Stress Twin */}
          <div className={`p-5 rounded-2xl border ${isDark ? "bg-[hsl(223,47%,9%)] border-amber-700/40" : "bg-white border-amber-200 shadow-sm"}`} data-testid="demo-stress-card">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-5 h-5 text-amber-500" />
              <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>⚡ {T.stress}</h3>
              <span className={`ml-auto text-2xl font-bold tabular-nums transition-colors ${scoreColor(state.stress_score)}`} data-testid="demo-stress-score">{state.stress_score}/100</span>
            </div>
            <div className={`h-2 rounded-full overflow-hidden mb-3 ${isDark ? "bg-slate-800" : "bg-slate-100"}`}>
              <div className={`h-full transition-all duration-500 ${bg_score_bar(state.stress_score)}`} style={{ width: `${state.stress_score}%` }} />
            </div>
            <div className={`p-2 rounded-lg text-center ${isDark ? "bg-slate-800/60" : "bg-amber-50"}`}>
              <div className="text-[9px] uppercase opacity-70 tracking-wider">{T.cortisol}</div>
              <div className={`text-base font-bold ${state.stress_score >= 75 ? "text-emerald-500" : state.stress_score >= 55 ? "text-amber-500" : "text-rose-500"}`}>
                {bg
                  ? (state.stress_score >= 75 ? "Балансиран" : state.stress_score >= 55 ? "Умерен" : "Висок")
                  : (state.stress_score >= 75 ? "Balanced"   : state.stress_score >= 55 ? "Moderate" : "High")}
              </div>
            </div>
            <p className={`text-[11px] mt-3 leading-relaxed ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              {bg
                ? (state.stress_score >= 75
                    ? "Парасимпатиков превес — отлично възстановяване."
                    : state.stress_score >= 55
                    ? "Умерен симпатиков тонус. Опитай дишане."
                    : "Висок симпатиков превес. HPA-ос активна.")
                : (state.stress_score >= 75
                    ? "Parasympathetic dominance — excellent recovery."
                    : state.stress_score >= 55
                    ? "Moderate sympathetic tone. Try breathing."
                    : "High sympathetic dominance. HPA axis active.")}
            </p>
          </div>
        </div>

        {/* Event triggers */}
        <div className="flex flex-wrap gap-2 justify-center mb-4" data-testid="demo-triggers">
          <button onClick={() => trigger("stress")} data-testid="demo-trigger-stress"
            className={`px-4 py-2 rounded-full text-xs font-bold transition-all hover:scale-105 ${isDark ? "bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/30" : "bg-rose-100 hover:bg-rose-200 text-rose-800 border border-rose-200"}`}>
            🔥 {T.eventStress}
          </button>
          <button onClick={() => trigger("coffee")} data-testid="demo-trigger-coffee"
            className={`px-4 py-2 rounded-full text-xs font-bold transition-all hover:scale-105 ${isDark ? "bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/30" : "bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-200"}`}>
            <Coffee className="w-3 h-3 inline mr-1" />{T.eventCoffee}
          </button>
          <button onClick={() => trigger("breath")} data-testid="demo-trigger-breath"
            className={`px-4 py-2 rounded-full text-xs font-bold transition-all hover:scale-105 ${isDark ? "bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-500/30" : "bg-emerald-100 hover:bg-emerald-200 text-emerald-800 border border-emerald-200"}`}>
            <Wind className="w-3 h-3 inline mr-1" />{T.eventBreath}
          </button>
          <button onClick={() => trigger("rest")} data-testid="demo-trigger-rest"
            className={`px-4 py-2 rounded-full text-xs font-bold transition-all hover:scale-105 ${isDark ? "bg-slate-700/40 hover:bg-slate-700/60 text-slate-300 border border-slate-600" : "bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200"}`}>
            ⊙ {T.eventRest}
          </button>
        </div>

        {/* Event log */}
        {log.length > 0 && (
          <div className={`p-3 rounded-xl text-center text-[11px] mb-5 ${isDark ? "bg-slate-900/60 border border-slate-800" : "bg-white border border-slate-200"}`} data-testid="demo-event-log">
            <div className="opacity-60 mb-1">{T.log_prefix}</div>
            {log.map((l, i) => (
              <span key={i} className={`inline-block mx-1 px-2 py-0.5 rounded-full ${i === 0 ? "bg-fuchsia-500 text-white font-bold" : isDark ? "bg-slate-800 text-slate-400" : "bg-slate-100 text-slate-600"}`}>
                {l.ts} · {l.name}
              </span>
            ))}
          </div>
        )}

        <div className="text-center">
          <Link to="/login" data-testid="demo-cta-btn" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-bold text-white shadow-md hover:shadow-lg transition-shadow" style={{ background: "var(--brand-gradient-primary)" }}>
            {T.cta}
          </Link>
        </div>
      </div>
    </section>
  );
}
